import { getFormErrorMessage } from "@core/utility/get-error-message";
import { TenantFormRule, TenantFormSchema } from "@factory/form/form.rule";
import { useFormDefaultTenant } from "@factory/form/form-default-tenant.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseTenantModel } from "@factory/model/query-model-tenant";
import {
  useQyDeleteTenant,
  useQyGetTenantById,
  useQyUpdateTenant,
} from "@factory/query/tenant.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditTenant() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultTenant();
  const navigate = useNavigate();
  const { mapUpdateTenant, mapDeleteTenant } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { tenantId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Tenant
  const handleGetApiSuccess = (data: GetResponseTenantModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("owner", responseData?.owner || "");
    setValue("slug", responseData?.slug || "");
    setValue("businessName", responseData?.business_name || "");
    setValue("businessType", responseData?.business_type || "");
    setValue("description", responseData?.description || "");
    setValue("avatar", responseData?.avatar || "");
    setValue("email", responseData?.email || "");
    setValue("mobileNumber", responseData?.mobile_number || "");
    setValue("phoneNumber", responseData?.phone_number || "");
    setValue("website", responseData?.website || "");
    setValue(
      "membershipDate",
      formatDate(responseData?.membership_date) as any,
      undefined
    );
    setValue("rewardsType", responseData?.rewards_type || "");
    setValue(
      "postRegistrationLink",
      responseData?.post_registration_link || ""
    );
    setValue(
      "postRegistrationMessage",
      responseData?.post_registration_message || ""
    );
    setValue("allowUnderEighteen", Boolean(responseData?.allow_under_eighteen) || false);
    setValue("allowDecimalPoints", Boolean(responseData?.allow_decimal_points) || false);
    setValue("featureQuota", Boolean(responseData?.feature_quota) || false);
    setValue("featureSplashScreen", Boolean(responseData?.feature_splash_screen) || false);
    setValue("featureCoupon", Boolean(responseData?.feature_coupon) || false);
    setValue("featureReferral", Boolean(responseData?.feature_referral) || false);
    setValue("featureBirthdayEmail", Boolean(responseData?.feature_birthday_email) || false);
    setValue("termsAndConditions", responseData?.terms_and_conditions);
    setValue("privacyPolicy", responseData?.privacy_policy);
    setValue("smtpAppPassword", responseData?.smtp_app_password);
    setValue("smtpMailUser", responseData?.smtp_mail_user);
    setValue("smtpMailFrom", responseData?.smtp_mail_from);
    setValue("smtpMailContent", responseData?.smtp_mail_content);
  };
  const {
    data: tenantResponse,
    isLoading,
    isError: tenantError,
  } = useQyGetTenantById(tenantId || "", handleGetApiSuccess);
  const tenantData = tenantResponse?.data?.[0];

  // API UPDATE Tenant
  const handleUpdateApiSuccess = () => {
    showSuccess("Tenant updated successfully");
  };
  const { mutate: updateTenant, isLoading: isEditLoading } = useQyUpdateTenant(
    handleUpdateApiSuccess
  );

  // API DELETE Tenant
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Tenant deleted successfully");
  };
  const { mutate: deleteTenant } = useQyDeleteTenant(handleDeleteApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<TenantFormSchema>({
    defaultValues: getDefault(tenantData),
    resolver: zodResolver(TenantFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: TenantFormSchema) => {
    if (!tenantData || !tenantData.code) {
      throw new Error("No tenant data");
    }

    const formData = mapUpdateTenant(form, tenantData.code);
    updateTenant(formData);
  };
  const handleValidateError = (err: FieldErrors<TenantFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!tenantId) {
      throw new Error("No tenant data found");
    }

    const form = mapDeleteTenant(tenantId);
    deleteTenant(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    tenantId,
    tenantData,
    isLoading,
    tenantError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
